import { makeAutoObservable, runInAction } from 'mobx';
import { RootStore } from '../../RootStore';
import { overtimeService } from '../../../services/modules/overtime/overtime.service';
import {
  IOvertimeParam,
  IOvertimeResult,
  IOvertimeUpdateParam,
} from '../../../models/overtime/OvertimeModel';
import { toast } from 'react-toastify';
import { GridApi } from 'ag-grid-community';

interface ODataParams {
  $filter?: string;
  $orderby?: string;
  $skip?: number;
  $top?: number;
  $select?: string;
  $count?: boolean;
}

interface GridParams {
  startRow: number;
  endRow: number;
  filterModel: any;
  sortModel: any;
}

export class OvertimeStore {
  rootStore: RootStore;
  overtimeList: IOvertimeResult[] = [];
  selectedOvertime: IOvertimeResult | null = null;
  error: string | null = null;
  gridApi: GridApi<IOvertimeResult> | null = null;
  selectedOvertimeToDelete: number | null = null;
  selectedUserId: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setGridApi = (api: GridApi<IOvertimeResult>) => {
    this.gridApi = api;
  };

  setSelectedOvertime = (overtime: IOvertimeResult | null) => {
    this.selectedOvertime = overtime;
  };

  setSelectedOvertimeToDelete = (id: number | null) => {
    this.selectedOvertimeToDelete = id;
  };

  setSelectedUserId = (userId: string | null) => {
    this.selectedUserId = userId;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  reset = () => {
    this.selectedOvertime = null;
    this.error = null;
    this.selectedOvertimeToDelete = null;
    this.selectedUserId = null;
  };

  refreshGrid = () => {
    if (this.gridApi) {
      this.gridApi.refreshServerSide();
      this.gridApi.refreshInfiniteCache();
      this.gridApi.redrawRows({ rowNodes: [] });
    }
  };

  listOvertimes = async (
    params: GridParams
  ): Promise<{ rows: IOvertimeResult[]; lastRow: number; count: number }> => {
    try {
      const odataParams: ODataParams = {
        $count: true,
        $skip: params.startRow,
        $top: params.endRow - params.startRow,
      };

      const filters: string[] = [];

      // Admin seçili bir kullanıcı seçtiyse onun kayıtlarını göster
      if (this.selectedUserId) {
        filters.push(`userId eq '${this.selectedUserId}'`);
      }

      if (params.filterModel) {
        const filterModelFilters = Object.entries(params.filterModel).map(
          ([field, filter]: [string, any]) => {
            const numericFields = ['id', 'userId', 'totalHours'];
            if (numericFields.includes(field)) {
              return `${field} eq ${filter.filter}`;
            }
            const value = String(filter.filter ?? '').toLowerCase().replace(/'/g, "''");
            return `contains(tolower(${field}), '${value}')`;
          }
        );

        filters.push(...filterModelFilters);
      }

      if (filters.length > 0) {
        odataParams.$filter = filters.join(' and ');
      }

      if (params.sortModel && params.sortModel.length > 0) {
        const { colId, sort } = params.sortModel[0];
        odataParams.$orderby = `${colId} ${sort === 'asc' ? 'asc' : 'desc'}`;
      }

      const response: any = await overtimeService.listOvertimes({
        params: odataParams,
      });

      return runInAction(() => {
        if (response) {
          const totalCount = response['@odata.count'] || 0;
          return {
            rows: response.value || [],
            lastRow: totalCount,
            count: totalCount,
          };
        }
        return {
          rows: [],
          lastRow: 0,
          count: 0,
        };
      });
    } catch (error) {
      console.error('Error fetching overtimes:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
      });
      throw error;
    }
  };

  createOvertime = async (data: IOvertimeParam) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await overtimeService.createOvertime(data);

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        toast.error(response.message || 'Mesai kaydı oluşturulamadı');
        return response;
      }

      runInAction(() => {
        this.error = null;
        this.refreshGrid();
      });

      toast.success('Mesai kaydı başarıyla oluşturuldu');
      return response;
    } catch (error) {
      console.error('Error creating overtime:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
        toast.error('Mesai kaydı oluşturulurken bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  updateOvertime = async (id: number, data: IOvertimeUpdateParam) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await overtimeService.updateOvertime(id, data);

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        toast.error(response.message || 'Mesai kaydı güncellenemedi');
        return response;
      }

      runInAction(() => {
        this.error = null;
        this.refreshGrid();
      });

      toast.success('Mesai kaydı başarıyla güncellendi');
      return response;
    } catch (error) {
      console.error('Error updating overtime:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
        toast.error('Mesai kaydı güncellenirken bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  deleteOvertime = async (id: number) => {
    try {
      this.rootStore.uiStore.setLoading(true);
      const response: any = await overtimeService.deleteOvertime(id);

      if (response.error) {
        runInAction(() => {
          this.error = response.message;
        });
        toast.error(response.message || 'Mesai kaydı silinemedi');
        return response;
      }

      runInAction(() => {
        this.error = null;
        this.refreshGrid();
      });

      toast.success('Mesai kaydı başarıyla silindi');
      return response;
    } catch (error) {
      console.error('Error deleting overtime:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
        toast.error('Mesai kaydı silinirken bir hata oluştu');
      });
      throw error;
    } finally {
      runInAction(() => {
        this.rootStore.uiStore.setLoading(false);
      });
    }
  };

  getUserOvertimes = async (userId: number): Promise<IOvertimeResult[]> => {
    try {
      const response = await overtimeService.getUserOvertimes(userId);
      runInAction(() => {
        this.overtimeList = response || [];
      });
      return response || [];
    } catch (error) {
      console.error('Error fetching user overtimes:', error);
      runInAction(() => {
        this.error = error instanceof Error ? error.message : 'An unknown error occurred';
      });
      return [];
    }
  };
}
